import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useTranslation } from "react-i18next";
import { createCollection, updateCollection } from "@/services/collections";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Loader2, Save } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useToast } from "@/hooks/use-toast";
import AppHeader from "@/components/AppHeader";
import SuccessCelebration from "@/components/card/SuccessCelebration";
import { supabase } from "@/integrations/supabase/client";

import StepPurpose from "@/components/wizard/StepPurpose";
import StepOccasion from "@/components/wizard/StepOccasion";
import StepDetails from "@/components/wizard/StepDetails";
import StepDesign from "@/components/wizard/StepDesign";
import StepChipIn from "@/components/wizard/StepChipIn";
import StepRsvp from "@/components/wizard/StepRsvp";
import StepCheckout from "@/components/wizard/StepCheckout";
import WizardSteps, { type WizardStepMeta } from "@/components/wizard/WizardSteps";
import type { RsvpQuestion } from "@/components/card/RsvpQuestionBuilder";
import { getGiftDataByRecipientEmail, type Wishlist } from "@/services/wishlists";
import { useTimezone } from "@/hooks/useTimezone";

export interface WizardData {
  type: "card_only" | "card_collection";
  cardFor: "myself" | "someone_else";
  cardFrom: string;
  occasion: string;
  templateId: string;
  templateUrl: string;
  customCoverUrl: string;
  customDesignJson: Record<string, unknown> | null;
  recipientName: string;
  title: string;
  messageToGroup: string;
  deadline: string;
  endTime: string;
  timezone: string;
  /** Transient UI flag — true once the user has picked "Sign only"
   *  or "Sign and chip in" on the first wizard step. Not persisted
   *  to the collections row; only used to gate Next on step 0 and
   *  to decide whether the Chip-in step is included in the flow. */
  purposeSelected: boolean;
  collectionEnabled: boolean;
  contributionType: "set_amount" | "any_amount" | "optional";
  setAmount: string;
  feeHandling: "organizer_pays" | "deduct_from_collection";
  recipientEmail: string;
  customSlug: string;
  // Gift guidance
  preferencesWants: string;
  preferencesHas: string;
  preferencesStyle: string;
  preferencesRegistries: string[];
  showPreferencesInInvite: boolean;
  recipientWishlists: Wishlist[];
  collectionGoal: boolean;
  collectionGoalAmount: string;
  collectionNote: string;
  showTotalCollected: boolean;
  musicId: string;
  // RSVP — optional wizard step. Same shape as the card-manage
  // SettingsModal's RSVP tab; fields flow through to the
  // collection's rsvp_* columns when enabled.
  rsvpEnabled: boolean;
  rsvpLocationName: string;
  rsvpLocationAddress: string;
  /** ISO string for the event date portion; time is stored
   *  separately as HH:mm and combined at save time. */
  rsvpEventDate: string;
  rsvpEventTime: string;
  rsvpPlusOne: boolean;
  /** Kept as a string so the Input type=number can render an
   *  empty state for "unlimited"; parsed to number at save. */
  rsvpMaxCapacity: string;
  rsvpInviteOnly: boolean;
  rsvpQuestions: RsvpQuestion[];
}

const initialData: WizardData = {
  type: "card_only",
  cardFor: "someone_else",
  cardFrom: "",
  occasion: "",
  templateId: "",
  templateUrl: "",
  customCoverUrl: "",
  customDesignJson: null,
  recipientName: "",
  title: "",
  messageToGroup: "",
  deadline: "",
  endTime: "17:00",
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  purposeSelected: false,
  collectionEnabled: false,
  contributionType: "any_amount",
  setAmount: "",
  feeHandling: "deduct_from_collection",
  recipientEmail: "",
  customSlug: "",
  preferencesWants: "",
  preferencesHas: "",
  preferencesStyle: "",
  preferencesRegistries: [],
  showPreferencesInInvite: true,
  recipientWishlists: [],
  collectionGoal: false,
  collectionGoalAmount: "",
  collectionNote: "",
  showTotalCollected: true,
  musicId: "",
  rsvpEnabled: false,
  rsvpLocationName: "",
  rsvpLocationAddress: "",
  rsvpEventDate: "",
  rsvpEventTime: "18:00",
  rsvpPlusOne: false,
  rsvpMaxCapacity: "",
  rsvpInviteOnly: false,
  rsvpQuestions: [],
};

/**
 * Fire the gift-preference invite email to the recipient. Runs best-
 * effort in the background after a card is successfully created —
 * we don't block the success overlay on it. The recipient lookup
 * (`getGiftDataByRecipientEmail`) tells us whether they already have
 * a joyapp profile so the email's CTA branches between "fill your
 * preferences" and "create an account first".
 *
 * Uses an idempotency key scoped to (collection, email) so repeated
 * attempts — e.g. a user re-entering the wizard from a draft and
 * hitting Create — don't spam the same recipient.
 */
async function sendGiftPreferenceInvite(args: {
  collectionId: string;
  recipientEmail: string;
  recipientName: string;
  organizerName: string;
  occasionTitle: string;
}) {
  try {
    const { data: giftData } = await getGiftDataByRecipientEmail(args.recipientEmail);
    const hasAccount = !!giftData?.profile;

    // Always use the published domain so recipients land on the real
    // site regardless of where the organiser created the card.
    const base = "https://joyapp.lovable.app";
    const profileUrl = `${base}/profile?tab=gift-preferences`;
    const signupUrl = `${base}/auth?next=${encodeURIComponent("/profile?tab=gift-preferences")}`;

    const { error } = await supabase.functions.invoke("send-transactional-email", {
      body: {
        templateName: "gift-preference-invitation",
        recipientEmail: args.recipientEmail,
        idempotencyKey: `gift-invite-${args.collectionId}-${args.recipientEmail.toLowerCase()}`,
        templateData: {
          recipientName: args.recipientName || undefined,
          organizerName: args.organizerName || undefined,
          occasionTitle: args.occasionTitle || undefined,
          profileUrl,
          signupUrl,
          hasAccount,
        },
      },
    });

    // Don't escalate to console.error — Sentry's console integration
    // captures that as a runtime error and the user's card was
    // created successfully either way. `console.warn` keeps the
    // message visible in devtools without polluting the error feed.
    // Common reasons this fails (and we still want the card flow to
    // succeed): the edge function hasn't been redeployed yet with
    // the new template registered, or the recipient is on the
    // suppression list.
    if (error) {
      console.warn("[sendGiftPreferenceInvite] edge function returned an error; skipping send:", error);
    }
  } catch (err) {
    // Same rationale as above — this path covers network failures
    // and other thrown errors from the invoke client.
    console.warn("[sendGiftPreferenceInvite] threw; skipping send:", err);
  }
}

/**
 * Build the Supabase insert/update payload from a WizardData snapshot.
 * Used by both the draft save path and the final-create path — keeping
 * it in one place means the two never drift out of sync.
 */
const buildCollectionPayload = (data: WizardData, organizerId: string, status: "draft" | "active") => {
  // Combine the RSVP date + time inputs into a single ISO string,
  // matching the format the card-manage SettingsModal writes.
  const rsvpEventIso: string | null = (() => {
    if (!data.rsvpEnabled || !data.rsvpEventDate) return null;
    const date = new Date(data.rsvpEventDate);
    if (Number.isNaN(date.getTime())) return null;
    const [hh, mm] = (data.rsvpEventTime || "18:00").split(":");
    date.setHours(parseInt(hh, 10) || 0, parseInt(mm, 10) || 0, 0, 0);
    return date.toISOString();
  })();

  return {
    organizer_id: organizerId,
    type: data.type,
    occasion: data.occasion || "",
    title: data.title,
    recipient_name: data.recipientName,
    recipient_email: data.recipientEmail || null,
    card_template_id: data.templateId || null,
    custom_cover_url: data.customCoverUrl || null,
    card_design_json: (data.customDesignJson as any) || null,
    message_to_group: data.messageToGroup || null,
    deadline: data.deadline ? new Date(data.deadline).toISOString() : null,
    timezone: data.timezone,
    collection_enabled: data.collectionEnabled,
    contribution_type: data.contributionType,
    set_amount: data.setAmount ? parseFloat(data.setAmount) : null,
    fee_handling: data.feeHandling,
    collection_goal: data.collectionGoal,
    collection_goal_amount: data.collectionGoalAmount ? parseFloat(data.collectionGoalAmount) : null,
    collection_note: data.collectionNote || null,
    show_total_collected: data.showTotalCollected,
    status,
    preferences_wants: data.preferencesWants || null,
    preferences_has: data.preferencesHas || null,
    preferences_style: data.preferencesStyle || null,
    preferences_registries: data.preferencesRegistries.filter((r) => r.trim()) as unknown as null,
    show_preferences_in_invite: data.showPreferencesInInvite,
    music_id: data.musicId || null,
    // RSVP — every column is zeroed out when the toggle is off so
    // a user flipping it back gets a clean slate.
    rsvp_enabled: data.rsvpEnabled,
    rsvp_location_name: data.rsvpEnabled ? data.rsvpLocationName.trim() || null : null,
    rsvp_location_address: data.rsvpEnabled ? data.rsvpLocationAddress.trim() || null : null,
    rsvp_event_date: rsvpEventIso,
    rsvp_plus_one: data.rsvpEnabled ? data.rsvpPlusOne : false,
    rsvp_questions: data.rsvpEnabled
      ? (data.rsvpQuestions.filter((q) => q.question.trim()) as unknown as null)
      : ([] as unknown as null),
    invite_only: data.rsvpEnabled ? data.rsvpInviteOnly : false,
    max_capacity:
      data.rsvpEnabled && data.rsvpMaxCapacity ? Number(data.rsvpMaxCapacity) : null,
  };
};

const CreateCard = () => {
  const [searchParams] = useSearchParams();
  const isGiftMe = searchParams.get("giftMe") === "1";
  const draftIdParam = searchParams.get("draft");

  const getInitialData = (): WizardData => {
    if (!isGiftMe) return initialData;
    const productName = searchParams.get("productName") || "";
    const productPrice = searchParams.get("productPrice") || "";
    const productUrl = searchParams.get("productUrl") || "";

    return {
      ...initialData,
      purposeSelected: true,
      collectionEnabled: true,
      type: "card_collection",
      contributionType: "any_amount",
      setAmount: productPrice,
      preferencesRegistries: productUrl ? [productUrl] : [],
      preferencesWants: productName || "",
    };
  };

  const [step, setStep] = useState(0);
  const [data, setData] = useState<WizardData>(getInitialData);
  const [creating, setCreating] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [createdSlug, setCreatedSlug] = useState<string | null>(null);
  const [templatePrice, setTemplatePrice] = useState<number | null>(null);
  // The live draft collection id — starts null (user hasn't saved yet),
  // gets set once the first "Save draft" click inserts a row, and is
  // reused for subsequent saves and the final activation update.
  const [draftId, setDraftId] = useState<string | null>(null);
  const [loadingDraft, setLoadingDraft] = useState(!!draftIdParam);
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation();
  const platformTimezone = useTimezone();

  const updateData = useCallback((updates: Partial<WizardData>) => {
    setData((prev) => ({ ...prev, ...updates }));
  }, []);

  // Sync platform timezone into wizard data on mount
  useEffect(() => {
    if (platformTimezone && data.timezone !== platformTimezone) {
      updateData({ timezone: platformTimezone });
    }
  }, [platformTimezone, data.timezone, updateData]);

  const normalizedRecipientEmail = useMemo(
    () => data.recipientEmail.trim().toLowerCase(),
    [data.recipientEmail],
  );

  useEffect(() => {
    if (!loading && !user) {
      navigate("/auth");
    }
  }, [loading, navigate, user]);

  // Draft resume — if the URL carries a `?draft=<id>` param, try to
  // load the matching draft row from Supabase and rehydrate the
  // wizard state. Only drafts owned by the signed-in user resolve.
  useEffect(() => {
    if (!draftIdParam || !user) return;
    let active = true;
    (async () => {
      const { data: draftRow, error } = await supabase
        .from("collections")
        .select("*")
        .eq("id", draftIdParam)
        .eq("organizer_id", user.id)
        .maybeSingle();
      if (!active) return;
      if (error || !draftRow) {
        toast({
          variant: "destructive",
          title: t("createCard.draftNotFound", { defaultValue: "Draft not found" }),
          description: t("createCard.draftNotFoundDesc", {
            defaultValue: "We couldn't find that draft. Starting a fresh card.",
          }),
        });
        setLoadingDraft(false);
        return;
      }
      // Hydrate wizard data from the draft row. Any fields the draft
      // doesn't have fall back to the initial defaults so the wizard
      // behaves the same as a fresh start for those steps.
      const row = draftRow as Record<string, any>;
      setData((prev) => ({
        ...prev,
        type: row.type ?? prev.type,
        occasion: row.occasion ?? "",
        templateId: row.card_template_id ?? "",
        customCoverUrl: row.custom_cover_url ?? "",
        customDesignJson: row.card_design_json ?? null,
        recipientName: row.recipient_name ?? "",
        recipientEmail: row.recipient_email ?? "",
        title: row.title ?? "",
        messageToGroup: row.message_to_group ?? "",
        deadline: row.deadline ? row.deadline.slice(0, 16) : "",
        timezone: row.timezone ?? prev.timezone,
        // Drafts are saved only after the user got past the purpose
        // step, so we treat the choice as already made on resume.
        purposeSelected: true,
        collectionEnabled: !!row.collection_enabled,
        contributionType: row.contribution_type ?? prev.contributionType,
        setAmount: row.set_amount != null ? String(row.set_amount) : "",
        feeHandling: row.fee_handling ?? prev.feeHandling,
        collectionGoal: !!row.collection_goal,
        collectionGoalAmount: row.collection_goal_amount != null ? String(row.collection_goal_amount) : "",
        collectionNote: row.collection_note ?? "",
        showTotalCollected: row.show_total_collected ?? true,
        musicId: row.music_id ?? "",
        preferencesWants: row.preferences_wants ?? "",
        preferencesHas: row.preferences_has ?? "",
        preferencesStyle: row.preferences_style ?? "",
        preferencesRegistries: Array.isArray(row.preferences_registries) ? row.preferences_registries : [],
        showPreferencesInInvite: row.show_preferences_in_invite ?? true,
        // RSVP rehydration — date and time live together as a single
        // ISO column on the DB side; split them apart here so the
        // step's date picker + time input render correctly.
        rsvpEnabled: !!row.rsvp_enabled,
        rsvpLocationName: row.rsvp_location_name ?? "",
        rsvpLocationAddress: row.rsvp_location_address ?? "",
        rsvpEventDate: row.rsvp_event_date ?? "",
        rsvpEventTime: row.rsvp_event_date
          ? new Date(row.rsvp_event_date).toTimeString().slice(0, 5)
          : "18:00",
        rsvpPlusOne: !!row.rsvp_plus_one,
        rsvpMaxCapacity: row.max_capacity != null ? String(row.max_capacity) : "",
        rsvpInviteOnly: !!row.invite_only,
        rsvpQuestions: Array.isArray(row.rsvp_questions) ? row.rsvp_questions : [],
      }));
      setDraftId(row.id);
      setLoadingDraft(false);
    })();
    return () => {
      active = false;
    };
  }, [draftIdParam, user, toast, t]);

  // Auto-import gift preferences from the recipient's profile when
  // they have one. The organizer never sees these fields in the
  // wizard (the dedicated step was removed) — they're written
  // through to the collection so signers see a snapshot of the
  // recipient's preferences as they were at card-creation time,
  // without waiting on the recipient to engage with our email.
  useEffect(() => {
    if (!normalizedRecipientEmail) {
      setData((prev) => ({ ...prev, recipientWishlists: [] }));
      return;
    }

    let active = true;

    const timeoutId = window.setTimeout(async () => {
      const { data: giftData } = await getGiftDataByRecipientEmail(normalizedRecipientEmail);
      if (!active) return;

      setData((prev) => {
        const nextRegistries = new Set(prev.preferencesRegistries.filter((item) => item.trim()));
        giftData?.profile?.preferences_registries?.forEach((url) => {
          if (typeof url === "string" && url.trim()) nextRegistries.add(url.trim());
        });

        giftData?.wishlists
          ?.filter((wishlist) => wishlist.type === "registry" || wishlist.type === "wishlist")
          .forEach((wishlist) => {
            const wishlistUrl = `${window.location.origin}/wishlists/${wishlist.id}`;
            nextRegistries.add(wishlistUrl);
          });

        return {
          ...prev,
          recipientWishlists: giftData?.wishlists ?? [],
          preferencesWants: prev.preferencesWants || giftData?.profile?.preferences_wants || "",
          preferencesHas: prev.preferencesHas || giftData?.profile?.preferences_has || "",
          preferencesStyle: prev.preferencesStyle || giftData?.profile?.preferences_style || "",
          preferencesRegistries: Array.from(nextRegistries),
        };
      });
    }, 400);

    return () => {
      active = false;
      window.clearTimeout(timeoutId);
    };
  }, [normalizedRecipientEmail]);

  const isPaidTemplate = templatePrice != null && templatePrice > 0;

  // Step metadata powers the `<WizardSteps />` indicator. The checkout
  // step only shows for paid templates so the indicator can count
  // total steps dynamically.
  // Wizard step list — gift preferences are no longer collected in
  // the wizard. The recipient is invited (by email) to fill their
  // own preferences from their joyapp profile after card creation,
  // and signers pull from there at view time.
  // Step list is dynamic: the Chip-in step only appears if the user
  // picked "Sign and chip in" on the purpose step. Keeping the key
  // list as the single source of truth means the step renderer below
  // can look up what to render by index without extra bookkeeping.
  const steps: WizardStepMeta[] = useMemo(() => {
    const base: WizardStepMeta[] = [
      { key: "purpose", label: t("createCard.steps.purpose", { defaultValue: "Purpose" }) },
      { key: "occasion", label: t("createCard.steps.occasion", { defaultValue: "Occasion" }) },
      { key: "details", label: t("createCard.steps.details", { defaultValue: "Details" }) },
      { key: "design", label: t("createCard.steps.design", { defaultValue: "Design" }) },
    ];
    if (data.collectionEnabled) {
      base.push({ key: "chipin", label: t("createCard.steps.chipIn", { defaultValue: "Chip-in" }) });
    }
    base.push({ key: "rsvp", label: t("createCard.steps.rsvp", { defaultValue: "RSVP" }) });
    if (isPaidTemplate) {
      base.push({ key: "checkout", label: t("createCard.steps.checkout", { defaultValue: "Checkout" }) });
    }
    return base;
  }, [data.collectionEnabled, isPaidTemplate, t]);
  const TOTAL_STEPS = steps.length;
  const currentStepKey = steps[step]?.key;

  // Fetch template price when templateId changes
  useEffect(() => {
    if (!data.templateId) {
      setTemplatePrice(null);
      return;
    }
    supabase
      .from("card_templates")
      .select("price")
      .eq("id", data.templateId)
      .single()
      .then(({ data: tmpl }) => {
        setTemplatePrice(tmpl?.price ?? null);
      });
  }, [data.templateId]);

  if (!loading && !user) {
    return null;
  }

  // Step validation is keyed on the step's identifier rather than its
  // index so the Chip-in step appearing/disappearing doesn't shift
  // what each case means.
  const canNext = (): boolean => {
    switch (currentStepKey) {
      case "purpose": return data.purposeSelected;
      case "occasion": return !!data.occasion;
      case "details": return !!data.recipientName;
      case "design": return !!data.templateId;
      case "chipin": return true;
      case "rsvp": return true;
      case "checkout": return true;
      default: return false;
    }
  };

  const handleNext = () => {
    if (step < TOTAL_STEPS - 1) {
      setStep(step + 1);
    } else {
      handleCreate();
    }
  };

  const handleBack = () => {
    if (step > 0) setStep(step - 1);
  };

  // Save-as-draft — on first click inserts a new collection row with
  // status="draft" and stashes the id. Subsequent clicks update the
  // same row so the user can save repeatedly without creating dupes.
  // The Dashboard surfaces draft rows so the user can jump back in.
  const handleSaveDraft = async () => {
    if (!user) return;
    setSavingDraft(true);
    const payload = buildCollectionPayload(data, user.id, "draft");

    if (draftId) {
      const { error } = await updateCollection(draftId, payload);
      if (error) {
        toast({ variant: "destructive", title: t("common.error"), description: error.message });
      } else {
        toast({
          title: t("createCard.draftSaved", { defaultValue: "Draft saved" }),
          description: t("createCard.draftSavedDesc", {
            defaultValue: "Pick up where you left off from your dashboard.",
          }),
        });
      }
    } else {
      const { data: created, error } = await createCollection(payload);
      if (error || !created) {
        toast({
          variant: "destructive",
          title: t("common.error"),
          description: error?.message || "Could not save draft",
        });
      } else {
        setDraftId(created.id);
        toast({
          title: t("createCard.draftSaved", { defaultValue: "Draft saved" }),
          description: t("createCard.draftSavedDesc", {
            defaultValue: "Pick up where you left off from your dashboard.",
          }),
        });
      }
    }
    setSavingDraft(false);
  };

  const handleCreate = async () => {
    if (!user) return;
    setCreating(true);

    // Save as draft if paid (will activate after payment), active if free
    const cardStatus: "draft" | "active" = isPaidTemplate ? "draft" : "active";
    const payload = buildCollectionPayload(data, user.id, cardStatus);

    // If the user started from a draft or clicked Save Draft during the
    // flow, we already have an id — just update it to flip status.
    let collectionId: string | null = draftId;
    let shareSlug: string | null = null;
    if (draftId) {
      const { error } = await updateCollection(draftId, payload);
      if (error) {
        toast({ variant: "destructive", title: t("common.error"), description: error.message });
        setCreating(false);
        return;
      }
      const { data: refreshed } = await supabase
        .from("collections")
        .select("share_slug")
        .eq("id", draftId)
        .maybeSingle();
      shareSlug = refreshed?.share_slug ?? null;
    } else {
      const { data: collection, error } = await createCollection(payload);
      if (error || !collection) {
        toast({
          variant: "destructive",
          title: t("common.error"),
          description: error?.message || "Could not create card",
        });
        setCreating(false);
        return;
      }
      collectionId = collection.id;
      shareSlug = collection.share_slug;
    }

    if (isPaidTemplate && collectionId) {
      // Redirect to Stripe for payment
      try {
        const { data: paymentData, error: paymentError } = await supabase.functions.invoke(
          "create-card-payment",
          { body: { collection_id: collectionId, template_id: data.templateId } }
        );
        if (paymentError || !paymentData?.url) {
          toast({ variant: "destructive", title: t("common.error"), description: "Failed to initiate payment" });
          setCreating(false);
          if (shareSlug) navigate(`/card/${shareSlug}/manage`);
          return;
        }
        const paymentWindow = window.open("", "_blank");
        if (paymentWindow) {
          paymentWindow.location.href = paymentData.url;
        } else {
          window.location.href = paymentData.url;
        }
        setCreating(false);
        return;
      } catch {
        toast({ variant: "destructive", title: t("common.error"), description: "Payment error" });
        setCreating(false);
        if (shareSlug) navigate(`/card/${shareSlug}/manage`);
        return;
      }
    }

    // Invite the recipient (by email) to fill their gift preferences
    // on joyapp. The wizard no longer asks the organiser for this —
    // it lives on the recipient's profile as the canonical source.
    // Happy path: recipient has an account → link drops them on
    // Profile → Gift Preferences. Otherwise the email prompts them
    // to sign up first and then fill it out.
    //
    // We fire this in the background without awaiting or blocking
    // the success overlay: if the edge function is rate-limited /
    // misconfigured the organiser still sees the card was created.
    if (data.recipientEmail && collectionId) {
      void sendGiftPreferenceInvite({
        collectionId,
        recipientEmail: data.recipientEmail,
        recipientName: data.recipientName,
        organizerName: user.user_metadata?.display_name || user.email || "",
        occasionTitle: data.title || data.recipientName || "a group card",
      });
    }

    setCreatedSlug(shareSlug);
    setCreating(false);
    setShowSuccess(true);
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <AnimatePresence>
        {showSuccess && (
          <SuccessCelebration
            type="card"
            onDone={() => navigate(`/card/${createdSlug}`)}
          />
        )}
      </AnimatePresence>
      <AppHeader onBack="/dashboard" />

      {/* Step indicator — replaces the old animated progress bar with
          a Typeform-style numbered stepper. Users can click any
          completed step to jump back. */}
      <div className="border-b border-border/60 bg-background/80 backdrop-blur-sm">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 sm:py-5">
          <WizardSteps
            steps={steps}
            currentStep={step}
            onStepClick={(i) => setStep(i)}
          />
        </div>
      </div>

      {/* Step content — single full-width column now that the right
          preview panel is gone. max-w-2xl keeps form inputs a
          comfortable reading width on desktop. Bottom padding leaves
          room for the fixed footer. */}
      <main className="flex-1 overflow-y-auto">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8 pb-32">
          {loadingDraft ? (
            <div className="flex items-center justify-center py-24 text-muted-foreground">
              <Loader2 className="animate-spin me-2" size={20} />
              {t("createCard.loadingDraft", { defaultValue: "Loading your draft…" })}
            </div>
          ) : (
            <AnimatePresence mode="wait">
              <motion.div
                key={step}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.2, ease: "easeOut" }}
              >
                {currentStepKey === "purpose" && <StepPurpose data={data} updateData={updateData} />}
                {currentStepKey === "occasion" && <StepOccasion data={data} updateData={updateData} />}
                {currentStepKey === "details" && <StepDetails data={data} updateData={updateData} />}
                {currentStepKey === "design" && <StepDesign data={data} updateData={updateData} />}
                {currentStepKey === "chipin" && <StepChipIn data={data} updateData={updateData} />}
                {currentStepKey === "rsvp" && <StepRsvp data={data} updateData={updateData} />}
                {currentStepKey === "checkout" && isPaidTemplate && (
                  <StepCheckout data={data} templatePrice={templatePrice} />
                )}
              </motion.div>
            </AnimatePresence>
          )}
        </div>
      </main>

      {/* Fixed footer — Back, Save draft, Next. Save draft is always
          visible (not gated on canNext) so users can bail at any point
          and resume later. */}
      <div className="fixed bottom-0 left-0 right-0 z-40 bg-background/95 backdrop-blur-sm border-t border-border">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-2">
          <Button
            variant="ghost"
            onClick={handleBack}
            disabled={step === 0 || creating || savingDraft}
            className="shrink-0"
          >
            <ArrowLeft size={16} className="me-1.5" />
            {t("createCard.back", { defaultValue: "Back" })}
          </Button>

          <Button
            variant="outline"
            onClick={handleSaveDraft}
            disabled={savingDraft || creating}
            className="shrink-0 gap-1.5 rounded-full"
          >
            {savingDraft ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <Save size={14} />
            )}
            <span className="hidden sm:inline">
              {t("createCard.saveDraft", { defaultValue: "Save draft" })}
            </span>
          </Button>

          <div className="flex-1" />

          <Button
            onClick={handleNext}
            disabled={creating || savingDraft || !canNext()}
            size="lg"
            className="rounded-full min-w-[8rem]"
          >
            {step === TOTAL_STEPS - 1
              ? isPaidTemplate
                ? creating ? t("createCard.creating") : t("wizard.checkout.payAndCreate", { defaultValue: "Pay & Create" })
                : creating ? t("createCard.creating") : t("wizard.chipIn.createCard")
              : t("createCard.next")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CreateCard;
